import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const MAP_IMG =
  "https://images.pexels.com/photos/41949/earth-earth-at-night-night-lights-41949.jpeg?auto=compress&cs=tinysrgb&w=1600";

const W = 1000;
const H = 500;

const project = (lat, lng) => ({
  x: ((lng + 180) / 360) * W,
  y: ((90 - lat) / 180) * H,
});

const SEV_COLOR = {
  low: "#00f0ff",
  medium: "#39d0ff",
  high: "#b026ff",
  critical: "#ff2d6f",
};

const Arc = ({ ev, i }) => {
  const s = project(ev.src.lat, ev.src.lng);
  const d = project(ev.dst.lat, ev.dst.lng);
  const mx = (s.x + d.x) / 2;
  const my = (s.y + d.y) / 2;
  const dist = Math.hypot(d.x - s.x, d.y - s.y);
  const cy = my - dist * 0.32;
  const path = `M ${s.x} ${s.y} Q ${mx} ${cy} ${d.x} ${d.y}`;
  const color = SEV_COLOR[ev.severity] || "#00f0ff";
  const delay = (i % 14) * 0.25;

  return (
    <g>
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={ev.real ? 2 : 1.2}
        strokeLinecap="round"
        style={{
          strokeDasharray: dist,
          strokeDashoffset: dist,
          animation: `drawArc 2.4s ease-out ${delay}s infinite`,
          filter: `drop-shadow(0 0 3px ${color})`,
          opacity: 0.85,
        }}
      />
      <circle r={ev.real ? 4 : 3} fill={color}>
        <animateMotion dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" path={path} />
      </circle>
      <circle cx={d.x} cy={d.y} r="3" fill={color} style={{ filter: `drop-shadow(0 0 5px ${color})` }}>
        <animate attributeName="r" values="2;7;2" dur="2s" begin={`${delay}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.2;1" dur="2s" begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
    </g>
  );
};

export const ThreatMap = ({ compact = false }) => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ attacks_per_min: 0, countries: 0, real_scans: 0 });
  const timer = useRef(null);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/attacks");
      setEvents(data.events.slice(0, 26));
      setStats(data.stats);
    } catch (e) {
      console.error("No se pudieron cargar los eventos del mapa de amenazas:", e);
    }
  };

  useEffect(() => {
    fetchData();
    timer.current = setInterval(fetchData, 4500);
    return () => clearInterval(timer.current);
  }, []);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#03050d]"
      data-testid="threat-map"
    >
      <style>{`@keyframes drawArc{0%{stroke-dashoffset:var(--d,1000);opacity:0}15%{opacity:.9}70%{opacity:.9}100%{stroke-dashoffset:0;opacity:0}}`}</style>

      <div className="relative" style={{ aspectRatio: "2 / 1" }}>
        <img src={MAP_IMG} alt="Mapa mundial" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-[#03050d]/40" />
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          {events.map((ev, i) => (
            <Arc key={ev.id} ev={ev} i={i} />
          ))}
        </svg>

        <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-black/50 px-3 py-1.5 backdrop-blur">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#ff2d6f]" />
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/80">
            En vivo · Visualización
          </span>
        </div>
      </div>

      {!compact && (
        <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10">
          {[
            { v: stats.attacks_per_min.toLocaleString(), l: "Ataques / min" },
            { v: stats.countries, l: "Países" },
            { v: stats.real_scans, l: "Escaneos reales" },
          ].map((s) => (
            <div key={s.l} className="px-4 py-5 text-center" data-testid="map-stat">
              <div className="font-head text-2xl font-bold text-white md:text-3xl">{s.v}</div>
              <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-white/50">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
